// src/lib/pushClient.js
//
// Browser side of Web Push: capability detection, permission, subscribe /
// unsubscribe, and the cadence preference round-trip.
//
// NOTE ON LOCATION: this lives in src/lib/ but is NOT one of the mirrored
// engine files. src/lib/planGenerator.js and src/lib/userBaseline.js are
// byte-identical copies of their server counterparts and are guarded by
// server/test/mirror.test.js (see CLAUDE.md §"Generator mirror"). This file
// has no server twin and is not covered by that guard.

import { api } from "../api";

/** Why push may be unavailable, phrased for a human. */
export function pushSupport() {
  if (typeof window === "undefined") return { ok: false, reason: "no-window" };
  if (!("serviceWorker" in navigator)) return { ok: false, reason: "no-service-worker" };
  if (!("PushManager" in window)) return { ok: false, reason: "no-push-manager" };
  if (!("Notification" in window)) return { ok: false, reason: "no-notification-api" };

  // iOS only delivers Web Push to a PWA installed on the home screen. In a
  // Safari tab PushManager exists but a subscription never fires, so detect
  // it and say so rather than letting the user think it worked.
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  if (isIOS && !standalone) return { ok: false, reason: "ios-needs-install" };

  return { ok: true };
}

export const SUPPORT_COPY = {
  "no-service-worker": "This browser does not support background notifications.",
  "no-push-manager": "This browser does not support push notifications.",
  "no-notification-api": "This browser does not support notifications.",
  "ios-needs-install": "On iPhone, add RESET to your Home Screen first — iOS only delivers notifications to installed apps.",
  "no-window": "Unavailable here.",
};

/** base64url VAPID key -> Uint8Array, which is what subscribe() requires. */
function urlBase64ToUint8Array(base64) {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(padded);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/** Is the server configured and willing to send? */
export async function fetchPushConfig() {
  try { return await api.get("/api/push/key"); }
  catch (_) { return { enabled: false, publicKey: null }; }
}

export async function currentSubscription() {
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch (_) { return null; }
}

/**
 * Ask permission, subscribe, register with the server.
 * Returns { ok } or { ok:false, reason } — "denied" is terminal until the
 * user changes it in browser settings, so the UI must say so.
 */
export async function enablePush() {
  const support = pushSupport();
  if (!support.ok) return { ok: false, reason: support.reason };

  const { enabled, publicKey } = await fetchPushConfig();
  if (!enabled || !publicKey) return { ok: false, reason: "server-disabled" };

  let permission = Notification.permission;
  if (permission === "default") permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, reason: "denied" };

  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,               // required by Chrome
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
    const j = sub.toJSON();
    await api.post("/api/push/subscribe", { endpoint: j.endpoint, keys: j.keys });
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: "subscribe-failed", detail: err.message };
  }
}

export async function disablePush() {
  try {
    const sub = await currentSubscription();
    if (sub) {
      await api.post("/api/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
      await sub.unsubscribe().catch(() => {});
    }
    return { ok: true };
  } catch (_) { return { ok: true }; }
}

/** Send one notification to this user's own devices, to prove the path. */
export async function sendTestPush() {
  try { return await api.post("/api/push/test", {}); }
  catch (err) { return { ok: false, error: err.body?.error || err.message }; }
}

/**
 * Persist cadence. The browser's IANA zone is captured here because the
 * server cannot know when "07:00 for this runner" is without it.
 */
export async function saveNotifySettings(patch) {
  let tz = null;
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch (_) {}
  const notify = Object.assign({ tz }, patch);
  const r = await api.patch("/api/me", { settings: { notify } });
  return r?.user?.settings?.notify || notify;
}
